import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Property } from '@vsolv/packages/properties/domain';
import { MenuTriggerForDirective } from '@vsolv/vectors-ui/menu';
import { PropertyInputComponent, getEffectiveMaxDate } from '../../abstract-property-input.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-date-property-input',
  templateUrl: './date-input.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DateInputComponent,
    },
    { provide: NG_VALIDATORS, useExisting: DateInputComponent, multi: true },
  ],
})
export class DateInputComponent extends PropertyInputComponent {
  @Input() override property!: Property.DateModel;

  @Input() showPicker = true;

  get minDateValue(): string {
    if (!this.property.config.min) return '';
    return new Date(this.property.config.min).toISOString().split('T')[0];
  }

  get maxDateValue(): string {
    return getEffectiveMaxDate(this.property).toISOString().split('T')[0];
  }

  override writeValue(value: unknown): void {
    if (value) {
      if (typeof value === 'string') {
        value = new Date(value).toISOString().split('T')[0];
      } else if (typeof value === 'object') {
        value = (value as Date).toISOString().split('T')[0];
      }
    }

    this.value = value;
    this.formControl?.setValue(value);
  }

  private clampYear(value: string): string {
    const [year, month, day] = value.split('-');
    const maxYear = this.maxDateValue.split('-')[0];
    const minYear = this.minDateValue?.split('-')[0];

    let clampedYear = year;
    if (clampedYear > maxYear) clampedYear = maxYear;
    if (minYear && clampedYear < minYear) clampedYear = minYear;

    return clampedYear !== year ? `${clampedYear}-${month}-${day}` : value;
  }

  populateDate(event: Date, menu: MenuTriggerForDirective) {
    const value = this.clampYear(formatDate(event, 'yyyy-MM-dd', 'en'));
    this.formControl.patchValue(value);
    menu.close();
  }

  onDateChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.value) return;

    const clamped = this.clampYear(input.value);
    if (clamped !== input.value) {
      input.value = clamped;
      this.formControl.setValue(clamped);
    }
  }
}
